import { TOASTER_OPTIONS } from '@/assets/js/config/client'
import http from '@/$plugins/http'

const PRODUCT_RESTOCK_SUBSCRIPTION_ENDPOINT = '/catalog/api/ProductRestockSubscription'

const getToastOption = (type) => {
  const fallback = {}
  const options = TOASTER_OPTIONS?.api?.options || fallback
  return Object.assign({}, options[type] || {})
}

const getErrorMessage = (error, fallbackMessage) => {
  const apiMessage = error?.response?.data?.message
  const axiosMessage = error?.message
  return apiMessage || axiosMessage || fallbackMessage
}

const isSubscriptionActive = (response) => {
  const result = response?.data?.result
  return !!(result && (result.id || result.productExternalId || result.isActive))
}

const showToast = ({ toast, message, variant }) => {
  if (!toast || !message) return
  const options = Object.assign({}, getToastOption(variant === 'danger' ? 'error' : 'send'))
  toast.toast(message, Object.assign(options, { variant }))
}

/**
 * @param {string} productExternalId
 * @returns {Promise<{ subscribed: boolean, error?: string }>}
 */
export const getProductRestockSubscriptionStatus = async (productExternalId) => {
  if (!productExternalId) {
    return { subscribed: false, error: 'missing-product-id' }
  }

  try {
    const statusResponse = await http({
      method: 'get',
      url: `${PRODUCT_RESTOCK_SUBSCRIPTION_ENDPOINT}/${encodeURIComponent(productExternalId)}/status`
    })
    return { subscribed: isSubscriptionActive(statusResponse) }
  } catch (error) {
    return { subscribed: false, error: 'status-failed' }
  }
}

/**
 * Subscribes (POST) if not subscribed, unsubscribes (DELETE) if already subscribed.
 * @returns {Promise<{ success: boolean, subscribed?: boolean, duplicate?: boolean, reason?: string, error?: unknown }>}
 */
export const toggleProductRestockSubscription = async ({ productExternalId, toast, t }) => {
  if (!productExternalId) {
    showToast({ toast, message: t('notifyMeError'), variant: 'danger' })
    return { success: false, reason: 'missing-product-id' }
  }

  let subscribed = false
  try {
    const statusResponse = await http({
      method: 'get',
      url: `${PRODUCT_RESTOCK_SUBSCRIPTION_ENDPOINT}/${encodeURIComponent(productExternalId)}/status`
    })
    subscribed = isSubscriptionActive(statusResponse)
  } catch (error) {
    subscribed = false
  }

  if (subscribed) {
    try {
      await http({
        method: 'delete',
        url: `${PRODUCT_RESTOCK_SUBSCRIPTION_ENDPOINT}/${encodeURIComponent(productExternalId)}`
      })
      showToast({ toast, message: t('notifyMeUnsubscribed'), variant: 'success' })
      return { success: true, subscribed: false }
    } catch (error) {
      showToast({ toast, message: getErrorMessage(error, t('notifyMeUnsubscribeError')), variant: 'danger' })
      return { success: false, reason: 'unsubscribe-failed', error, subscribed: true }
    }
  }

  try {
    await http({
      method: 'post',
      url: PRODUCT_RESTOCK_SUBSCRIPTION_ENDPOINT,
      data: {
        productExternalId
      }
    })

    showToast({ toast, message: t('notifyMeSuccess'), variant: 'success' })
    return { success: true, subscribed: true, duplicate: false }
  } catch (error) {
    showToast({ toast, message: getErrorMessage(error, t('notifyMeError')), variant: 'danger' })
    return { success: false, reason: 'request-failed', error, subscribed: false }
  }
}

export const subscribeToProductRestock = async (opts) => toggleProductRestockSubscription(opts)
